import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import type { RingPayload } from '@/redux/app/actions/calls';

const Prompt = styled.div`
  width: 320px;
  background: var(--color-surface, #fff);
  border: 1px solid var(--color-border, #d9d9d9);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  padding: 16px;
  pointer-events: auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const PulsingDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #2ecc71;
  animation: calls-ring-pulse 1s ease-in-out infinite;

  @keyframes calls-ring-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
`;

const ContactName = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const Detail = styled.div`
  font-size: 13px;
  color: var(--color-text-secondary, #666);
  margin-bottom: 12px;
`;

const Buttons = styled.div`
  display: flex;
  gap: 8px;
`;

const AnswerButton = styled.button`
  flex: 1;
  background: #2ecc71;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const DeclineButton = styled.button`
  background: transparent;
  color: var(--color-text-secondary, #666);
  border: 1px solid var(--color-border, #d9d9d9);
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
`;

export interface IncomingCallPromptProps {
  ring: RingPayload;
  claiming: boolean;
  onAnswer: () => void;
  onDecline: () => void;
  // Called once the ring window has closed without an answer or a server stop
  onExpire?: () => void;
}

// The server broadcasts call_ring_stop when the ring window closes; give that
// event a moment to arrive before the prompt retires itself.
export const EXPIRY_GRACE_MS = 2000;

const remainingSeconds = (ring: RingPayload) => Math.max(
  0,
  Math.ceil(ring.started_at + ring.timeout_seconds - Date.now() / 1000),
);

export const IncomingCallPrompt = ({
  ring, claiming, onAnswer, onDecline, onExpire,
}: IncomingCallPromptProps) => {
  const [remaining, setRemaining] = useState(() => remainingSeconds(ring));
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    const interval = setInterval(() => setRemaining(remainingSeconds(ring)), 1000);
    return () => clearInterval(interval);
  }, [ring]);

  // Safety net for a missed call_ring_stop (websocket hiccup, or the prompt was
  // restored from the store after the window closed): never show a stale ring.
  // A claim in flight resolves the prompt on its own, so leave it alone then.
  useEffect(() => {
    if (remaining > 0 || claiming) return;
    const timeout = setTimeout(() => onExpireRef.current?.(), EXPIRY_GRACE_MS);
    return () => clearTimeout(timeout);
  }, [remaining, claiming]);

  return (
    <Prompt role="alertdialog" aria-label="Incoming call">
      <Header>
        <PulsingDot />
        Incoming Call
      </Header>
      <ContactName>{ring.contact_name || ring.contact_phone}</ContactName>
      <Detail>
        {ring.contact_name ? `${ring.contact_phone} · ` : ''}
        {remaining}s
      </Detail>
      <Buttons>
        <AnswerButton onClick={onAnswer} disabled={claiming}>
          {claiming ? 'Connecting…' : 'Answer'}
        </AnswerButton>
        <DeclineButton onClick={onDecline} disabled={claiming}>
          Decline
        </DeclineButton>
      </Buttons>
    </Prompt>
  );
};
